import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ArrowLeft, AlertCircle, CheckCircle, Coins } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const AXIS_COST = 50;

export default function AxisAssessmentPage() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg p-10 text-center">
          <AlertCircle className="w-12 h-12 text-gold mx-auto mb-4" />
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Login Required</h1>
          <p className="font-dm text-navy/60 text-sm mb-6">Please log in to take the AXIS Assessment.</p>
          <Link to="/login" className="inline-block bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors">Log In</Link>
        </div>
      </div>
    );
  }

  const balance = user.assessment_tokens ?? 0;

  if (balance < AXIS_COST && !iframeUrl) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg p-10 text-center" data-testid="insufficient-tokens-card">
          <Coins className="w-12 h-12 text-gold mx-auto mb-4" />
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Not Enough Assessment Tokens</h1>
          <p className="font-dm text-navy/60 text-sm mb-2">You need <strong>{AXIS_COST} assessment tokens</strong> to take the AXIS Assessment.</p>
          <p className="font-dm text-navy/60 text-sm mb-6">Your current balance: <strong className="text-gold">{balance} tokens</strong></p>
          <Link to="/pricing#assessment-packs" className="inline-block bg-navy text-gold font-dm font-bold px-8 py-3 rounded-lg hover:bg-navy/90 transition-colors" data-testid="buy-tokens-cta">
            Buy Assessment Tokens
          </Link>
        </div>
      </div>
    );
  }

  const handleStart = async () => {
    setStarting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/assessments/axis/start`, {}, { withCredentials: true });
      setIframeUrl(res.data.iframe_url);
      await refreshUser();
      setConfirming(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to start assessment");
    } finally {
      setStarting(false);
    }
  };

  if (iframeUrl) {
    return (
      <div className="min-h-screen bg-cream pt-20">
        <div className="bg-white border-b border-navy/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-playfair text-navy text-xl font-bold">AXIS Assessment</h1>
            <p className="font-dm text-navy/50 text-xs">In progress · 50 assessment tokens used</p>
          </div>
          <Link to="/dashboard" className="font-dm text-navy/70 text-sm hover:text-gold transition-colors flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> View My Results
          </Link>
        </div>
        <iframe
          src={iframeUrl}
          title="AXIS Assessment"
          className="w-full"
          style={{ height: "calc(100vh - 140px)", border: "none" }}
          data-testid="axis-iframe"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-32 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/assessments" className="inline-flex items-center gap-2 text-navy/60 hover:text-gold font-dm text-sm mb-6 transition-colors" data-testid="back-to-assessments">
          <ArrowLeft className="w-4 h-4" /> Back to Assessments
        </Link>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-navy p-8 text-center">
            <Sparkles className="w-10 h-10 text-gold mx-auto mb-3" />
            <h1 className="font-playfair text-white text-3xl font-bold mb-2">AXIS Assessment</h1>
            <p className="font-dm text-gold/80 text-sm">Understand Your Core Behavioural Drives</p>
          </div>

          <div className="p-8 space-y-6">
            <div>
              <h2 className="font-playfair text-navy text-xl font-bold mb-3">Before you begin</h2>
              <ul className="space-y-2.5">
                <li className="font-dm text-navy/70 text-sm flex gap-3">
                  <span className="text-gold font-bold">▸</span>
                  This assessment takes approximately <strong>12 minutes</strong> to complete.
                </li>
                <li className="font-dm text-navy/70 text-sm flex gap-3">
                  <span className="text-gold font-bold">▸</span>
                  Your results will be saved to your dashboard under <strong>My Assessments</strong>.
                </li>
                <li className="font-dm text-navy/70 text-sm flex gap-3">
                  <span className="text-gold font-bold">▸</span>
                  Cost: <strong className="text-gold">{AXIS_COST} assessment tokens</strong> (you have <strong>{balance} tokens</strong>)
                </li>
                <li className="font-dm text-navy/70 text-sm flex gap-3">
                  <span className="text-gold font-bold">▸</span>
                  Tokens are deducted only after you confirm. The assessment opens in a secure embedded view.
                </li>
              </ul>
            </div>

            {!confirming ? (
              <button onClick={() => setConfirming(true)} data-testid="proceed-axis-btn"
                className="w-full bg-gold text-navy font-dm font-bold py-4 rounded-lg text-base hover:bg-gold/90 transition-colors">
                Proceed to Assessment
              </button>
            ) : (
              <div className="bg-cream border-2 border-gold rounded-xl p-6">
                <h3 className="font-dm text-navy font-bold mb-2">Confirm Token Deduction</h3>
                <p className="font-dm text-navy/70 text-sm mb-5">
                  This assessment will use <strong>{AXIS_COST} assessment tokens</strong> from your account. You currently have <strong>{balance} tokens</strong>. After this assessment, you'll have <strong>{balance - AXIS_COST} tokens</strong> remaining.
                </p>
                {error && <p className="font-dm text-red-600 text-sm mb-3">{error}</p>}
                <div className="flex gap-3">
                  <button onClick={handleStart} disabled={starting} data-testid="confirm-axis-btn"
                    className="flex-1 bg-navy text-gold font-dm font-bold py-3 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">
                    {starting ? "Starting..." : "Confirm & Start"}
                  </button>
                  <button onClick={() => setConfirming(false)} className="font-dm text-navy/60 px-6 py-3 hover:text-navy transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
